import React from 'react';
import { noop } from 'lodash';
import { DownlossTypeCode } from '../shared/quote-or-design-form/rooms/heatLoss/downlossTypeCode';
import { UnitFormModel } from '../shared/quote-or-design-form/units/unitFormModel';

type FloorHeatLossFieldValue = {
  temperature: string;
  downlossTypeCode: DownlossTypeCode;
};

export type UnitFloorHeatLossFieldValue = {
  manifoldRoomValues: Array<Array<FloorHeatLossFieldValue>>;
} & FloorHeatLossFieldValue;

export type UnitHeatLossFieldValue = {
  manifoldRoomValues: Array<Array<string>>;
  value: string | undefined;
};

export type DesignFormDefaultsValue = {
  unitRoofElements: Array<UnitHeatLossFieldValue>;
  setUnitRoofElements: React.Dispatch<React.SetStateAction<Array<UnitHeatLossFieldValue>>>;
  unitFloorElements: Array<UnitFloorHeatLossFieldValue>;
  setUnitFloorElements: React.Dispatch<React.SetStateAction<Array<UnitFloorHeatLossFieldValue>>>;
  unitWallElements: Array<UnitHeatLossFieldValue>;
  setUnitWallElements: React.Dispatch<React.SetStateAction<Array<UnitHeatLossFieldValue>>>;
};

export const getInitialUnitWallHeatLossDetailsFormModel = (unit: UnitFormModel): Array<UnitHeatLossFieldValue> => {
  if (unit.heatLossDetails == null) {
    return [];
  } else {
    const unitWallElementForms = unit.heatLossDetails.wallElements;

    return unitWallElementForms.map((wallElement, wallIndex) => {
      const unitWallElements: Array<Array<string>> | null = unit.manifolds.map(manifold => {
        return (manifold.rooms || []).map(
          room =>
            room.heatLossDetails?.walls[wallIndex].externalAirTemperatureInDegreesCelsius ??
            wallElement.defaultExternalAirTemperatureInDegreesCelsius,
        );
      });

      return {
        manifoldRoomValues: unitWallElements,
        value: wallElement.defaultExternalAirTemperatureInDegreesCelsius,
      };
    });
  }
};

export const getInitialUnitRoofHeatLossDetailsFormModel = (unit: UnitFormModel): Array<UnitHeatLossFieldValue> => {
  if (unit.heatLossDetails == null) {
    return [];
  } else {
    const unitRoofElementForms = unit.heatLossDetails.roofElements;

    return unitRoofElementForms.map((roofElement, roofIndex) => {
      const roomRoofElements: Array<Array<string>> | null = unit.manifolds.map(manifold => {
        return (manifold.rooms || []).map(
          room =>
            room.heatLossDetails?.roofs[roofIndex].temperatureAboveInDegreesCelsius ??
            roofElement.defaultTemperatureAboveInDegreesCelsius,
        );
      });

      return {
        manifoldRoomValues: roomRoofElements,
        value: roofElement.defaultTemperatureAboveInDegreesCelsius,
      };
    });
  }
};

export const getInitialUnitFloorHeatLossDetailsFormModel = (
  unit: UnitFormModel,
): Array<UnitFloorHeatLossFieldValue> => {
  if (unit.heatLossDetails == null) {
    return [];
  } else {
    const unitFloorElementModels = unit.heatLossDetails.floorElements;

    return unitFloorElementModels.map((floorElement, floorIndex) => {
      const roomFloorElements: Array<Array<FloorHeatLossFieldValue>> | null = unit.manifolds.map(manifold => {
        return (manifold.rooms || []).map(room => ({
          temperature:
            room.heatLossDetails?.floors[floorIndex].temperatureBelowInDegreesCelsius ??
            floorElement.defaultTemperatureBelowInDegreesCelsius,
          downlossTypeCode: floorElement.downlossTypeCode,
        }));
      });

      return {
        manifoldRoomValues: roomFloorElements,
        temperature: floorElement.defaultTemperatureBelowInDegreesCelsius,
        downlossTypeCode: floorElement.downlossTypeCode,
      };
    });
  }
};

export const DesignFormDefaultsContext = React.createContext<DesignFormDefaultsValue>({
  unitRoofElements: [],
  setUnitRoofElements: () => noop,
  unitFloorElements: [],
  setUnitFloorElements: () => noop,
  unitWallElements: [],
  setUnitWallElements: () => noop,
});
